import { useEffect, useMemo, useRef, useState } from 'react'
import Icon, { itineraryIcon } from './Icon'
import { ITINERARY_TYPES, typeLabel } from '../lib/itineraryTypes'
import { formatDayLong, formatTime } from '../lib/format'
import { toDateInputValue, toTimeInputValue, browserTimeZone } from '../lib/zonedTime'
import { updateItineraryItem } from '../lib/mutations'
import type { ItineraryDraft } from '../lib/mutations'
import type { ItineraryItem, WithId } from '../types/models'

/**
 * An itinerary item, edited where it sits.
 *
 * Changing a soundcheck by 15 minutes used to be: click row, wait for a modal,
 * find the field, change it, hit Save, wait for the modal to close. That's five
 * steps and a context switch for a thing that happens twenty times a day on the
 * road. Here the fields become editable in place, a Save appears only once
 * something has actually changed, and the write goes straight to Firestore —
 * every other client has it before the button finishes animating.
 */
export default function ItineraryCard({
  item,
  tourId,
  userId,
  open,
  onToggle,
  canEdit,
  onDelete,
}: {
  item: WithId<ItineraryItem>
  tourId: string
  userId: string
  open: boolean
  onToggle: () => void
  canEdit: boolean
  onDelete: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<ItineraryDraft>(() => draftFrom(item))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const firstFieldRef = useRef<HTMLInputElement>(null)

  const original = useMemo(() => draftFrom(item), [item])

  // A live update from another client while this row is open shouldn't be
  // clobbered — but it also shouldn't wipe out what's being typed here, so it
  // only reseeds when the field isn't being edited.
  useEffect(() => {
    if (!editing) setDraft(draftFrom(item))
  }, [item, editing])

  useEffect(() => {
    if (editing) firstFieldRef.current?.focus()
  }, [editing])

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original],
  )

  const patch = (next: Partial<ItineraryDraft>) =>
    setDraft((previous) => ({ ...previous, ...next }))

  async function save() {
    if (!dirty || !draft.title.trim()) return
    setSaving(true)
    setError(null)
    try {
      await updateItineraryItem(item.id, draft, tourId, item.ownerId || userId)
      setEditing(false)
      setJustSaved(true)
      window.setTimeout(() => setJustSaved(false), 1600)
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setSaving(false)
    }
  }

  function cancel() {
    setDraft(original)
    setEditing(false)
    setError(null)
  }

  return (
    <div
      className="panel"
      style={{
        overflow: 'hidden',
        borderColor: editing ? 'var(--accent)' : undefined,
        transition: 'border-color 0.15s var(--ease)',
      }}
    >
      {/* ------------------------------------------------------ collapsed */}
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="row"
        style={{ width: '100%', padding: '16px 18px', gap: 16, textAlign: 'left' }}
      >
        <Icon
          name={itineraryIcon(item.type)}
          size={19}
          style={{ color: 'var(--text-secondary)' }}
        />
        <span className="grow" style={{ minWidth: 0 }}>
          <span className="truncate" style={{ display: 'block', fontWeight: 600 }}>
            {item.title}
          </span>
          {item.subtitle && (
            <span className="t-caption truncate" style={{ display: 'block' }}>
              {item.subtitle}
            </span>
          )}
        </span>

        {justSaved && <span className="chip chip-positive">Saved</span>}

        <span className="stack" style={{ gap: 1, alignItems: 'flex-end' }}>
          {item.needsTime ? (
            <span className="chip chip-accent">Set time</span>
          ) : (
            <span className="t-timecode">{formatTime(item.timeUTC, item.timezone)}</span>
          )}
          {item.timezone && !item.needsTime && (
            <span className="t-spec-caption faint">
              {shortZone(item.timezone, item.timeUTC.toDate())}
            </span>
          )}
        </span>
      </button>

      {/* ------------------------------------------------------- expanded */}
      {open && (
        <div
          style={{
            padding: '14px 18px 16px 53px',
            borderTop: '1px solid var(--hairline)',
          }}
        >
          {editing ? (
            <div className="stack" style={{ gap: 12 }}>
              <InlineField label="Title">
                <input
                  ref={firstFieldRef}
                  className="field"
                  value={draft.title}
                  onChange={(event) => patch({ title: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void save()
                    if (event.key === 'Escape') cancel()
                  }}
                />
              </InlineField>

              <div
                className="form-row"
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}
              >
                <InlineField label="Type">
                  <select
                    className="field"
                    value={draft.type}
                    onChange={(event) => patch({ type: event.target.value })}
                  >
                    {ITINERARY_TYPES.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                </InlineField>
                <InlineField label="Date">
                  <input
                    className="field"
                    type="date"
                    value={draft.date}
                    onChange={(event) => patch({ date: event.target.value })}
                  />
                </InlineField>
                <InlineField label="Time">
                  <input
                    className="field"
                    type="time"
                    value={draft.time}
                    onChange={(event) => patch({ time: event.target.value })}
                  />
                </InlineField>
              </div>

              <InlineField label="Subtitle">
                <input
                  className="field"
                  value={draft.subtitle ?? ''}
                  onChange={(event) => patch({ subtitle: event.target.value })}
                />
              </InlineField>

              <InlineField label="Notes">
                <textarea
                  className="field"
                  rows={2}
                  style={{ resize: 'vertical' }}
                  value={draft.notes ?? ''}
                  onChange={(event) => patch({ notes: event.target.value })}
                />
              </InlineField>

              {error && (
                <p role="alert" className="t-caption" style={{ color: 'var(--destructive)' }}>
                  {error}
                </p>
              )}

              <div className="row" style={{ gap: 8 }}>
                {/* Save only exists once there's something to save. An always-on
                    Save invites a click that does nothing. */}
                {dirty && (
                  <button
                    className="btn btn-primary"
                    onClick={() => void save()}
                    disabled={saving || !draft.title.trim()}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                )}
                <button className="btn btn-quiet" onClick={cancel}>
                  {dirty ? 'Discard' : 'Done'}
                </button>
                <span className="grow" />
                <span className="t-spec-caption faint">
                  {dirty ? 'Unsaved changes' : 'Enter to save · Esc to close'}
                </span>
              </div>
            </div>
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 12,
                }}
              >
                <Detail label="Type" value={typeLabel(item.type)} />
                <Detail
                  label="When"
                  value={
                    item.needsTime
                      ? 'Not scheduled yet'
                      : formatDayLong(item.timeUTC, item.timezone)
                  }
                />
                <Detail label="Timezone" value={item.timezone ?? '—'} />
                {item.isShowTiming && <Detail label="Part of" value="Show running order" />}
              </div>

              {item.notes && <Detail label="Notes" value={item.notes} />}

              {canEdit && (
                <div className="row" style={{ gap: 6 }}>
                  <IconAction
                    label="Edit this item"
                    icon="edit"
                    onClick={() => setEditing(true)}
                  />
                  <IconAction label="Delete this item" icon="trash" onClick={onDelete} danger />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ----------------------------------------------------------------- pieces */

function InlineField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="stack" style={{ gap: 4 }}>
      <span className="label" style={{ margin: 0 }}>
        {label}
      </span>
      {children}
    </label>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack" style={{ gap: 1 }}>
      <span className="t-eyebrow">{label}</span>
      <span className="t-body">{value}</span>
    </div>
  )
}

function IconAction({
  label,
  icon,
  onClick,
  danger,
}: {
  label: string
  icon: 'edit' | 'trash'
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 34,
        height: 34,
        borderRadius: 'var(--radius-control)',
        border: '1px solid var(--hairline)',
        background: 'var(--panel-raised)',
        color: danger ? 'var(--destructive)' : 'var(--text-secondary)',
      }}
    >
      <Icon name={icon} size={16} />
    </button>
  )
}

function draftFrom(item: WithId<ItineraryItem>): ItineraryDraft {
  const zone = item.timezone ?? browserTimeZone()
  return {
    title: item.title,
    type: item.type,
    date: toDateInputValue(item.timeUTC.toDate(), zone),
    // An item waiting for a time opens blank rather than showing the midnight
    // it was parked at.
    time: item.needsTime ? '' : toTimeInputValue(item.timeUTC.toDate(), zone),
    timezone: zone,
    subtitle: item.subtitle ?? '',
    notes: item.notes ?? '',
  }
}

function shortZone(zone: string, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(at)
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}
